import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';
import QtyStepper from '../orizon-ui/QtyStepper';
import { NumberField, SelectField, TextareaField, InfoBox, FormSection } from '../orizon-ui/FormField';
import FormModal, { useFormSalva } from './FormModal';
import { configScorta, formattaQta, formattaData } from '../gru/scorta';
import {
  colataLista, colataRegistraCarico, colataRegistraRettifica, nuovaChiaveIdempotenza, mapColataError,
} from '../../lib/services/colataService';
import { useToast } from '../orizon-ui/Toast';

const arrotonda3 = (n) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000;

const decimaliDi = (testo) => {
  const s = String(testo ?? '').replace(',', '.');
  const i = s.indexOf('.');
  return i === -1 ? 0 : s.length - i - 1;
};

/** Riga riassuntiva dell'articolo in testa ai modal di giacenza. */
const IntestazioneArticolo = ({ articolo }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl bg-muted/20 border border-border/40">
    <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
      <Icon name="Package" size={22} />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-black uppercase tracking-tight text-foreground break-words">{articolo.nome}</p>
      <p className="text-[10px] font-mono font-bold text-muted-foreground mt-0.5">
        Codice {articolo.codice} · giacenza attuale {formattaQta(articolo.qta, articolo.unita_decimali)} {articolo.unita_label}
      </p>
    </div>
  </div>
);

// ─── Carico (e carico iniziale) ──────────────────────────────────────────────

/**
 * Registra un ingresso di merce con colata_registra_carico.
 * iniziale=true: si usa subito dopo la creazione di un articolo ("Registra carico iniziale?", pulsante Salta).
 * Altrimenti permette di collegare il carico a un ordine aperto dello stesso articolo.
 */
export const CaricoModal = ({ articolo, iniziale = false, ordineId: ordineIniziale = '', onClose, onDone }) => {
  const toast = useToast();
  const [chiave] = useState(() => nuovaChiaveIdempotenza());
  const [qta, setQta] = useState('');
  const [motivo, setMotivo] = useState(iniziale ? 'Carico iniziale' : '');
  const [ordineId, setOrdineId] = useState(ordineIniziale || '');
  const [ordini, setOrdini] = useState([]);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  const decimali = Number(articolo.unita_decimali ?? 0);

  useEffect(() => {
    if (iniziale) return undefined;
    let annullato = false;
    colataLista('interventi', {
      perPagina: 50,
      soloAttivi: false,
      sort: { key: 'data_intervento', direction: 'desc' },
      filtri: { articolo_id: articolo.id, stato: 'ordinato' },
    })
      .then((res) => {
        if (annullato) return;
        setOrdini(res.righe);
        // ricezione di un ordine preselezionato: proponi la quantità ordinata
        const preselezionato = res.righe.find((o) => o.id === ordineIniziale);
        if (preselezionato) setQta((attuale) => (attuale === '' ? String(preselezionato.qta_residua ?? preselezionato.qta) : attuale));
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articolo.id, iniziale]);

  const scegliOrdine = (id) => {
    setOrdineId(id);
    pulisci('ordine');
    const o = ordini.find((x) => x.id === id);
    if (o && qta === '') setQta(String(o.qta_residua ?? o.qta));
  };

  const invia = async () => {
    const nuovi = {};
    const n = Number(String(qta).replace(',', '.'));
    if (qta === '' || !Number.isFinite(n) || n <= 0) nuovi.qta = 'Inserisci una quantità maggiore di 0';
    else if (decimaliDi(qta) > decimali) {
      nuovi.qta = decimali === 0 ? 'Solo quantità intere' : `Massimo ${decimali} decimali`;
    }
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    await salva(
      () => colataRegistraCarico({
        chiave,
        articoloId: articolo.id,
        qta: n,
        motivo: motivo.trim() || null,
        ordineId: ordineId || null,
      }),
      (r) => `Carico registrato: giacenza ${formattaQta(r.qta, decimali)} ${articolo.unita_label}.${r.ordine_stato === 'ricevuto' ? ' Ordine ricevuto.' : r.ordine_stato === 'ordinato' ? ' L\'ordine resta aperto con il residuo.' : ''}`,
      onDone,
    );
  };

  const opzioniOrdini = ordini.map((o) => ({
    value: o.id,
    label: `Ordine del ${formattaData(o.data_ordine || o.data_intervento)} · residuo ${formattaQta(o.qta_residua ?? o.qta, decimali)} di ${formattaQta(o.qta, decimali)} ${articolo.unita_label}`
      + `${o.data_consegna_prevista ? ` · consegna ${formattaData(o.data_consegna_prevista)}` : ''}`,
  }));

  return (
    <FormModal
      titolo={iniziale ? 'Registra carico iniziale?' : 'Registra carico'}
      etichetta={iniziale ? 'Nuovo articolo creato' : 'Giacenza · Ingresso merce'}
      larghezza="md"
      sporco={!iniziale && (qta !== '' || motivo !== '')}
      salvando={salvando}
      salvaLabel="Registra carico"
      annullaLabel={iniziale ? 'Salta' : 'Annulla'}
      onClose={onClose}
      onSubmit={invia}
    >
      <IntestazioneArticolo articolo={articolo} />

      {iniziale && (
        <InfoBox>
          L'articolo è stato creato con giacenza 0. Se è già in magazzino, registra la quantità presente: verrà salvata come movimento di carico nello storico.
        </InfoBox>
      )}

      <NumberField
        label="Quantità da caricare" name="qta" value={qta}
        onChange={(v) => { setQta(v); pulisci('qta'); }}
        suffisso={articolo.unita_label} obbligatorio errore={errori.qta}
        min={0} step={decimali === 0 ? '1' : 'any'}
      />

      {!iniziale && opzioniOrdini.length > 0 && (
        <SelectField
          label="Collega a un ordine aperto" name="ordine" value={ordineId}
          onChange={scegliOrdine} opzioni={opzioniOrdini} segnaposto="— Nessun ordine —"
          errore={errori.ordine}
          suggerimento="Se il carico copre il residuo l'ordine passa a Ricevuto, altrimenti resta aperto con la quantità residua."
        />
      )}

      <TextareaField
        label="Nota" name="motivo" value={motivo}
        onChange={setMotivo} placeholder="es. Arrivo merce, DDT n. ..." righe={2}
        errore={errori.motivo}
      />
    </FormModal>
  );
};

// ─── Rettifica ───────────────────────────────────────────────────────────────

/**
 * Corregge la giacenza (colata_registra_rettifica). Si imposta la quantità finale con lo stepper;
 * la differenza viene registrata come movimento "Rettifica" con motivo obbligatorio.
 */
export const RettificaModal = ({ articolo, onClose, onDone }) => {
  const [chiave] = useState(() => nuovaChiaveIdempotenza());
  const decimali = Number(articolo.unita_decimali ?? 0);
  const originale = Number(articolo.qta ?? 0);
  const [nuova, setNuova] = useState(originale);
  const [motivo, setMotivo] = useState('');
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  const delta = arrotonda3(nuova - originale);

  const statoNuovo = nuova <= 0 ? 'esaurito' : nuova < Number(articolo.qta_min ?? 0) ? 'sotto_scorta' : 'ok';
  const cfg = configScorta(statoNuovo);

  const invia = async () => {
    const nuovi = {};
    if (delta === 0) nuovi.qta = 'Modifica la quantità';
    if (motivo.trim() === '') nuovi.motivo = 'Motivo obbligatorio';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    await salva(
      () => colataRegistraRettifica({ chiave, articoloId: articolo.id, delta, motivo: motivo.trim() }),
      (r) => `Rettifica registrata: giacenza ${formattaQta(r.qta, decimali)} ${articolo.unita_label}.`,
      onDone,
    );
  };

  return (
    <FormModal
      titolo="Rettifica giacenza"
      etichetta="Giacenza · Correzione"
      larghezza="md"
      sporco={delta !== 0 || motivo !== ''}
      salvando={salvando}
      salvaLabel="Registra rettifica"
      onClose={onClose}
      onSubmit={invia}
    >
      <IntestazioneArticolo articolo={articolo} />

      <FormSection titolo="Nuova giacenza" icona="Box">
        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Quantità finale</span>
          <QtyStepper
            value={nuova}
            onChange={(v) => { setNuova(v); pulisci('qta'); }}
            originale={originale}
            decimali={decimali}
            colore={cfg.colore}
            unitaLabel={articolo.unita_label}
            disabilitato={salvando}
          />
          {errori.qta && (
            <p role="alert" className="text-[10px] font-black uppercase tracking-widest text-red-500/80 flex items-center gap-1.5">
              <Icon name="AlertCircle" size={11} />
              {errori.qta}
            </p>
          )}
        </div>

        {delta !== 0 && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border border-primary/15">
            <Icon name="ArrowRightLeft" size={12} className="text-primary/60 shrink-0" />
            <span className="text-[10px] font-black uppercase tracking-widest text-primary/60">
              Movimento:&nbsp;
              <span className={delta > 0 ? 'text-emerald-500' : 'text-red-500'}>
                Rettifica {delta > 0 ? '+' : ''}{formattaQta(delta, decimali)} {articolo.unita_label}
              </span>
            </span>
          </div>
        )}
      </FormSection>

      <TextareaField
        label="Motivo della rettifica" name="motivo" value={motivo}
        onChange={(v) => { setMotivo(v); pulisci('motivo'); }}
        placeholder="es. Differenza inventario, merce danneggiata..."
        obbligatorio errore={errori.motivo} righe={3}
      />
    </FormModal>
  );
};

